import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { startCheckout } from "@/lib/checkout";
import { toast } from "sonner";
import { HeartHandshake, ShieldCheck, Receipt, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const PRESETS = [501, 1001, 2501, 5001, 11001, 25001];

export default function Donate() {
  const [amount, setAmount] = useState(1001);
  const [custom, setCustom] = useState("");
  const [donor, setDonor] = useState({ name: "", email: "", phone: "", pan: "", address: "" });
  const [ngo, setNgo] = useState({});
  const [status, setStatus] = useState({ donations_enabled: false });
  const [busy, setBusy] = useState(false);
  const [receiptId, setReceiptId] = useState(null);

  useEffect(() => {
    api.get("/public/ngo").then((r) => setNgo(r.data || {})).catch(() => {});
    api.get("/payment-status").then((r) => setStatus(r.data || {})).catch(() => {});
  }, []);

  const finalAmount = custom ? parseInt(custom) || 0 : amount;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!donor.name.trim() || !donor.phone.trim() || !donor.email.trim()) {
      toast.error("Please fill your name, phone and email");
      return;
    }
    if (finalAmount < 10) { toast.error("Minimum donation is ₹10"); return; }
    if (finalAmount >= 2000 && !donor.pan.trim()) {
      toast.error("PAN is required for donations of ₹2,000 or more (to claim 80G)");
      return;
    }
    setBusy(true);
    try {
      await startCheckout({
        kind: "donation",
        amount: finalAmount,
        donor,
        return_to: "/donate",
        onSuccess: (txn) => {
          setBusy(false);
          setReceiptId(txn?.id || null);
          toast.success("Thank you for your donation! Receipt is being emailed to you.");
        },
        onFailure: () => setBusy(false),
        onDismiss: () => setBusy(false),
      });
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  if (!status.donations_enabled) {
    return (
      <div className="max-w-2xl mx-auto px-5 md:px-8 py-24 text-center" data-testid="donate-disabled">
        <HeartHandshake className="brand-orange mx-auto mb-6" size={56} />
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter mb-4">Donations coming soon.</h1>
        <p className="text-white/70">We are wrapping up the 80G paperwork. Please check back shortly — meanwhile, feel free to <Link to="/contact" className="brand-blue underline">reach out</Link> to support the academy.</p>
      </div>
    );
  }

  if (receiptId) {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return (
      <div className="max-w-2xl mx-auto px-5 md:px-8 py-24 text-center" data-testid="donate-thanks">
        <div className="w-16 h-16 mx-auto rounded-full bg-brand-orange flex items-center justify-center mb-6">
          <HeartHandshake size={30} className="text-black" />
        </div>
        <h1 className="font-display text-5xl font-black uppercase tracking-tighter mb-4">Thank you!</h1>
        <p className="text-lg text-white/70 mb-8">Your donation of <b className="brand-orange">₹{finalAmount.toLocaleString("en-IN")}</b> has been received. Your 80G tax-exemption receipt is ready to download.</p>
        <a href={`${base}/api/donations/${receiptId}/receipt`} target="_blank" rel="noreferrer"
          data-testid="donate-receipt-btn"
          className="inline-flex items-center gap-3 bg-brand-orange text-black px-8 py-4 font-bold uppercase tracking-widest text-sm hover:glow-orange">
          <Receipt size={16} /> Download 80G Receipt
        </a>
        <div className="mt-10">
          <Link to="/" className="text-white/60 hover:text-white text-sm font-mono uppercase tracking-widest">← Back to homepage</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-16" data-testid="donate-page">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Support the Academy</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95] mb-6">
            Fuel the next<br /><span className="brand-orange">champion.</span>
          </h1>
          <p className="text-white/70 leading-relaxed mb-8 max-w-lg">
            {ngo.legal_name || "HTP Sports Hub"} is a registered NGO. Every rupee you contribute goes to
            scholarships, equipment, and coaching for underprivileged athletes. Donations are eligible
            for tax exemption under Section 80G.
          </p>

          <form onSubmit={submit} className="space-y-6 bg-[#131A2B] border border-white/12 p-6 md:p-8">
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-white/40 mb-3">Choose an amount</div>
              <div className="grid grid-cols-3 gap-2">
                {PRESETS.map((v) => (
                  <button
                    type="button"
                    key={v}
                    onClick={() => { setAmount(v); setCustom(""); }}
                    data-testid={`donate-preset-${v}`}
                    className={`py-3 font-display font-black text-lg border transition ${
                      amount === v && !custom ? "border-brand-orange bg-brand-orange/15 brand-orange" : "border-white/20 text-white/80 hover:border-white/40"
                    }`}
                  >
                    ₹{v.toLocaleString("en-IN")}
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <div className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">Or enter a custom amount</div>
                <div className="flex items-center border border-white/20">
                  <span className="px-3 text-white/50 font-mono">₹</span>
                  <input
                    type="number" min="10"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    data-testid="donate-custom"
                    placeholder="Custom amount"
                    className="flex-1 bg-transparent py-3 pr-3 outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="font-mono text-xs uppercase tracking-widest text-white/40">Your details</div>
              <input value={donor.name} onChange={(e) => setDonor({ ...donor, name: e.target.value })}
                placeholder="Full name" data-testid="donate-name"
                className="w-full bg-transparent border border-white/20 py-3 px-4" />
              <div className="grid grid-cols-2 gap-3">
                <input value={donor.phone} onChange={(e) => setDonor({ ...donor, phone: e.target.value })}
                  placeholder="Phone" data-testid="donate-phone"
                  className="w-full bg-transparent border border-white/20 py-3 px-4" />
                <input value={donor.email} onChange={(e) => setDonor({ ...donor, email: e.target.value })}
                  placeholder="Email" type="email" data-testid="donate-email"
                  className="w-full bg-transparent border border-white/20 py-3 px-4" />
              </div>
              <input value={donor.pan} onChange={(e) => setDonor({ ...donor, pan: e.target.value.toUpperCase() })}
                placeholder="PAN (required for 80G, ₹2,000+)" data-testid="donate-pan"
                className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono" />
              <input value={donor.address} onChange={(e) => setDonor({ ...donor, address: e.target.value })}
                placeholder="Address (for receipt)" data-testid="donate-address"
                className="w-full bg-transparent border border-white/20 py-3 px-4" />
            </div>

            <button
              type="submit"
              disabled={busy || finalAmount < 10}
              data-testid="donate-submit"
              className="w-full bg-brand-orange text-black py-4 font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-3 hover:glow-orange disabled:opacity-50"
            >
              {busy ? "Opening payment…" : (
                <>Donate ₹{finalAmount.toLocaleString("en-IN")} securely <ArrowRight size={16} /></>
              )}
            </button>
            <div className="text-[11px] text-white/40 text-center flex items-center justify-center gap-1">
              <ShieldCheck size={12} /> Secured by Razorpay · UPI, cards, netbanking, wallets
            </div>
          </form>
        </div>

        {/* Right rail — receipt + trust */}
        <aside className="lg:col-span-2 space-y-6">
          <div className="border border-white/12 p-6 bg-[#131A2B]">
            <div className="font-display text-lg uppercase font-bold brand-orange mb-3">80G Tax Benefit</div>
            <p className="text-sm text-white/60 leading-relaxed">
              Donations to <b className="text-white">{ngo.legal_name || "HTP Sports Hub"}</b> qualify for a
              50% deduction on your taxable income under Section 80G of the Income-tax Act, 1961.
            </p>
            {ngo.section_80g_reg && (
              <div className="mt-4 text-xs font-mono text-white/50">
                80G Reg: <span className="text-white/80">{ngo.section_80g_reg}</span>
              </div>
            )}
            {ngo.section_12a_reg && (
              <div className="text-xs font-mono text-white/50">
                12A Reg: <span className="text-white/80">{ngo.section_12a_reg}</span>
              </div>
            )}
          </div>

          <div className="border border-white/12 p-6 bg-[#131A2B]">
            <div className="font-display text-lg uppercase font-bold brand-blue mb-3">Where your money goes</div>
            <ul className="text-sm text-white/60 space-y-2">
              <li>• Scholarships for underprivileged wards</li>
              <li>• New coaching equipment & maintenance</li>
              <li>• Tournament travel & lodging support</li>
              <li>• Free trial sessions for community kids</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
